/**
 * Миниатюра упражнения — одна на все экраны.
 *
 * Зачем. Человек видит название («Тяга горизонтального блока») и не всегда
 * знает, что за ним стоит. Картинка отвечает на это раньше слов. Сначала она
 * появилась в выборе замены, потом её попросили «всегда»: в правке тренировки,
 * в составе программы, на «Сегодня», на тренировке, в истории. Копия на каждом
 * экране рано или поздно разошлась бы — поэтому миниатюра живёт здесь.
 *
 * ЭТО ОТДЕЛЬНАЯ КНОПКА. Посмотреть и сделать — разные действия, а рядом с
 * картинкой всегда стоит что-то необратимое или просто другое: выбор замены,
 * «Убрать», тап по подходу в истории. Поэтому миниатюра — своя кнопка со своей
 * зоной касания, и вкладывать её в чужую кнопку нельзя.
 *
 * ВЕС. Миниатюра — отдельный файл в 120 px (эскиз самого Викисклада, ~6 КБ), и
 * грузится она, только когда кнопка видна или вот-вот станет видна. Полагаться
 * на `loading="lazy"` нельзя: внутри прокручиваемого блока браузер решает сам и
 * тянет больше.
 *
 * НЕТ РИСУНКА — И НЕ ЗАГРУЗИЛСЯ. Это разные вещи, и путать их на экране нельзя.
 * «?» значит «рисунка этого движения в свободных источниках нет»: у него есть
 * только текст техники. Рисунок, который есть, но не скачался (нет связи в
 * зале), помечается отдельно, иначе приложение соврало бы, что картинки нет
 * вовсе. У личного движения кнопки нет: технику за человека приложение не
 * сочиняет.
 */

import { useEffect, useState } from 'react'

import { TECHNIQUE_IMAGES } from '../technique'
import { hasImages, hasTechnique } from './TechniqueCard'

/**
 * Минимум, по которому рисуется миниатюра.
 *
 * Не весь `Exercise`: на «Целях» движение приходит вариантом цели, в «Истории» —
 * номером из подхода. `is_custom` необязателен: личные движения живут с номера
 * 1 000 000, и подсказок по ним нет в принципе.
 */
export interface ExerciseRef {
  id: number
  name: string
  is_custom?: boolean
}

/** Адрес миниатюры. Выводится из id: хранить его вторым полем незачем. */
export function thumbUrl(exerciseId: number): string | null {
  return hasImages(exerciseId) ? `/technique/thumb/${exerciseId}.png` : null
}

/** Есть ли что открыть по этому движению: текст техники есть у всех каталожных. */
export function hasPeek(exercise: ExerciseRef): boolean {
  return !exercise.is_custom && hasTechnique(exercise.id)
}

/**
 * Близко ли кнопка к видимой области — чтобы грузить только нужное.
 *
 * Узел приходит через callback-ref, а не через `useRef`. Экземпляр на экране
 * тренировки переживает смену движения: первым может стоять личное движение, у
 * которого кнопки нет вовсе. С `useRef` эффект отработал бы один раз на пустом
 * узле и больше не подписался бы — у следующего движения осталась бы пустая
 * рамка навсегда.
 */
function useNearViewport<T extends HTMLElement>(): [(node: T | null) => void, boolean] {
  const [node, setNode] = useState<T | null>(null)
  // Без IntersectionObserver (очень старый браузер) грузим сразу: пустые
  // рамки вместо картинок хуже лишних килобайт.
  const [near, setNear] = useState(typeof IntersectionObserver === 'undefined')

  useEffect(() => {
    if (near || !node) return
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          setNear(true)
          observer.disconnect()
        }
      },
      // Корень — ближайший блок, который прокручивается сам (список выбора,
      // список движений цели). Там страница стоит, а строки уезжают внутри
      // блока. Везде остальное прокручивается окно — корень по умолчанию.
      { root: node.closest('[data-thumbs-root]'), rootMargin: '160px 0px' },
    )
    observer.observe(node)
    return () => observer.disconnect()
  }, [near, node])

  return [setNode, near]
}

export function ExerciseThumb({
  exercise,
  onPeek,
  expanded,
  controls,
}: {
  exercise: ExerciseRef
  /** Кнопка передаётся, чтобы экран мог удержать её под пальцем. */
  onPeek: (button: HTMLButtonElement) => void
  expanded?: boolean
  controls?: string
}) {
  const [ref, near] = useNearViewport<HTMLButtonElement>()
  const [broken, setBroken] = useState(false)
  // Номер попытки — ключ картинки. Новый элемент перезапрашивает тот же
  // адрес; менять сам адрес нельзя, по нему рисунок лежит в кеше.
  const [attempt, setAttempt] = useState(0)

  // Не скачалось — пробуем снова, когда связь могла вернуться. Одного
  // 'online' мало: на iOS «сеть без интернета» его не меняет, а человек,
  // вышедший из подвала, чаще просто возвращается в приложение.
  useEffect(() => {
    if (!broken) return
    const retry = () => {
      setBroken(false)
      setAttempt((n) => n + 1)
    }
    const onVisible = () => {
      if (document.visibilityState === 'visible') retry()
    }
    window.addEventListener('online', retry)
    document.addEventListener('visibilitychange', onVisible)
    return () => {
      window.removeEventListener('online', retry)
      document.removeEventListener('visibilitychange', onVisible)
    }
  }, [broken])

  // Все хуки — выше этой строки: число хуков не должно зависеть от движения.
  if (!hasPeek(exercise)) return null

  const url = thumbUrl(exercise.id)
  // Рисунок изображает вариант движения (другой хват, другая постановка).
  // Правило проекта: вариант показывается только с честной пометкой — и
  // пометка обязана быть видна уже на миниатюре, а не только в просмотре.
  const variant = Boolean(TECHNIQUE_IMAGES[exercise.id]?.note)

  return (
    <button
      ref={ref}
      type="button"
      className="xp-thumb"
      aria-expanded={expanded}
      aria-controls={controls}
      aria-label={
        broken
          ? `Рисунок не загрузился: ${exercise.name}. Открыть технику`
          : `Как выглядит и как делать: ${exercise.name}`
      }
      onClick={(event) => {
        if (broken) {
          setBroken(false)
          setAttempt((n) => n + 1)
        }
        onPeek(event.currentTarget)
      }}
    >
      {url === null ? (
        <span className="xp-glyph" aria-hidden="true">
          ?
        </span>
      ) : (
        !broken &&
        near && (
          <img
            key={attempt}
            src={url}
            alt=""
            width={48}
            height={48}
            decoding="async"
            onError={() => setBroken(true)}
          />
        )
      )}
      {url !== null && broken && <span className="xp-offline">нет связи</span>}
      {url !== null && !broken && variant && <span className="xp-variant">вариант</span>}
    </button>
  )
}

/** Пустое место размером с миниатюру: у повтора движения подряд выравнивание не прыгает. */
export function ThumbGap() {
  return <span className="xp-thumb-gap" aria-hidden="true" />
}

/**
 * Скачать миниатюры заранее — для движений, до которых человек ещё не долистал.
 *
 * На тренировке следующее упражнение не видно, а в зале связи часто нет.
 * Через `Image`, а не `fetch`: запрос идёт тем же путём, что у `<img>`, и
 * ложится туда же — в кеш service worker'а.
 */
export function prefetchThumbs(exerciseIds: number[]): void {
  setTimeout(() => {
    for (const id of new Set(exerciseIds)) {
      const url = thumbUrl(id)
      if (url === null) continue
      const image = new Image()
      image.decoding = 'async'
      image.src = url
    }
  }, 0)
}
